import { AxiosError } from "axios";

/**
 * Collects human-readable API error text from Compax-style responses, e.g.
 * `[{"message":"link already used"}]` as JSON array, string body, or object.
 */
export const getAuthApiErrorText = (error: unknown): string => {
  const parts: string[] = [];

  if (error instanceof AxiosError) {
    parts.push(extractAuthApiErrorPayload(error.response?.data));
    if (error.message) {
      parts.push(error.message);
    }
  } else if (error instanceof Error) {
    parts.push(error.message);
    parts.push(extractAuthApiErrorPayload(error));
  } else if (error !== undefined && error !== null) {
    parts.push(extractAuthApiErrorPayload(error));
  }

  return parts.filter(Boolean).join(" ").toLowerCase();
};

const extractAuthApiErrorPayload = (payload: unknown): string => {
  if (payload === undefined || payload === null) {
    return "";
  }

  if (typeof payload === "string") {
    const trimmed = payload.trim();

    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        return extractAuthApiErrorPayload(JSON.parse(trimmed));
      } catch {
        return trimmed;
      }
    }

    return trimmed;
  }

  if (Array.isArray(payload)) {
    return payload.map(extractAuthApiErrorPayload).filter(Boolean).join(" ");
  }

  if (typeof payload === "object") {
    const record = payload as Record<string, unknown>;
    const messages: string[] = [];

    if (typeof record.message === "string") {
      messages.push(record.message);
    }

    if (typeof record.error_description === "string") {
      messages.push(record.error_description);
    }

    if (typeof record.error === "string") {
      messages.push(record.error);
    }

    if (messages.length > 0) {
      return messages.join(" ");
    }

    try {
      return JSON.stringify(payload);
    } catch {
      return "";
    }
  }

  return String(payload);
};

type AuthApiErrorItem = {
  message?: string;
  category?: string;
};

export const parseAuthApiError = (
  error: unknown,
): AuthApiErrorItem | undefined => {
  if (!(error instanceof AxiosError)) {
    return undefined;
  }

  return findAuthApiErrorItem(error.response?.data);
};

const findAuthApiErrorItem = (
  payload: unknown,
): AuthApiErrorItem | undefined => {
  if (payload === undefined || payload === null) {
    return undefined;
  }

  if (typeof payload === "string") {
    const trimmed = payload.trim();

    if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
      try {
        return findAuthApiErrorItem(JSON.parse(trimmed));
      } catch {
        return undefined;
      }
    }

    return undefined;
  }

  if (Array.isArray(payload)) {
    for (const item of payload) {
      const parsed = findAuthApiErrorItem(item);

      if (parsed?.message || parsed?.category) {
        return parsed;
      }
    }

    return undefined;
  }

  if (typeof payload === "object") {
    const record = payload as AuthApiErrorItem;

    if (record.message || record.category) {
      return record;
    }
  }

  return undefined;
};
